// Pure types modules, not the package barrels, so the archive mutation stays
// importable from Vitest without dragging the chat runtime in.
import {
  buildFeedbackDismissal,
  isAskUserFeedbackStagedAction,
  parseAskUserFeedbackInput,
} from '@renderer/chat/integrations/ask-user-feedback/types'
import {
  buildQuestionDismissal,
  isAskUserQuestionStagedAction,
  parseAskUserQuestionInput,
} from '@renderer/chat/integrations/ask-user-question/types'
import type { StagedAction } from './types'

/**
 * The post a dismissed ask owes the run waiting on it, or null when it owes
 * nothing (HIG-6042).
 *
 * Both tools end their turn on the card and only wake on their resume endpoint,
 * so archiving one without posting leaves the run waiting forever. A question
 * is the sharper case: its run is *blocked*, and the card deliberately has no
 * Skip, so the dismissal is the only thing that can release it.
 *
 * The two endpoints differ, hence the discriminant. Null for a card already
 * resolved, and for an input this client can't parse — archiving still has to
 * work for those, it just has nothing coherent to post.
 */
export type AnswerableDismissal =
  | { endpoint: 'feedback'; answer: ReturnType<typeof buildFeedbackDismissal> }
  | { endpoint: 'answer'; answer: ReturnType<typeof buildQuestionDismissal> }

export function answerableDismissalFor(action: StagedAction | undefined): AnswerableDismissal | null {
  if (!action || action.outputData != null) return null

  if (isAskUserFeedbackStagedAction(action)) {
    const input = parseAskUserFeedbackInput(action.inputData)
    return input ? { endpoint: 'feedback', answer: buildFeedbackDismissal(input.blanks) } : null
  }

  if (isAskUserQuestionStagedAction(action)) {
    const input = parseAskUserQuestionInput(action.inputData)
    return input ? { endpoint: 'answer', answer: buildQuestionDismissal(input.questions) } : null
  }

  return null
}

/**
 * A non-OK response from an answerable endpoint, carrying the status so callers
 * can tell an expected refusal (400: already resolved) from a real failure.
 *
 * Declared here rather than in `endpoints.ts` so the release policy below stays
 * loadable without React, auth, or `getChatApiRoute`.
 */
export class AnswerableEndpointError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'AnswerableEndpointError'
  }
}

/**
 * Tell the run waiting on a dismissed ask to stop waiting.
 *
 * Resolves quietly on a 400, which is the card this exists for: the server has
 * already resolved it, so there is no run left to wake and the archive is the
 * whole fix. Rethrows anything else, so the caller reports the failures that
 * are worth looking at.
 */
export async function releaseAnswerableRun(
  action: StagedAction | undefined,
  post: (endpoint: AnswerableDismissal['endpoint'], answer: AnswerableDismissal['answer']) => Promise<unknown>,
): Promise<void> {
  const dismissal = answerableDismissalFor(action)
  if (!dismissal) return
  try {
    await post(dismissal.endpoint, dismissal.answer)
  } catch (error) {
    if (error instanceof AnswerableEndpointError && error.status === 400) return
    throw error
  }
}
